import {useCallback} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {useWebSocket} from "../../contexts/WebSocketContext";
import {useImproveAxios} from "../../hooks/useImproveAxios";
import {getImproveAPI, type AnnouncementVideoProgressMessage, type LessonVideoProgressMessage} from "../gen";
import {getLessonForCourseQueryKey} from "./courses";
import {
	ANNOUNCEMENTS_QUERY_KEYS,
	patchLearnerAssignedEnrollmentVideoTime,
	patchLearnerNotificationEnrollmentVideoTime
} from "./announcements";

/** Client → server event; matches improve-api `LESSON_WEBSOCKET_EVENTS.VIDEO_PROGRESS` (not in OpenAPI). */
export const LESSON_VIDEO_PROGRESS_SOCKET_EVENT = "lesson.video.progress" as const;

/** Client → server event; matches improve-api `ANNOUNCEMENT_WEBSOCKET_EVENTS.VIDEO_PROGRESS` (not in OpenAPI). */
export const ANNOUNCEMENT_VIDEO_PROGRESS_SOCKET_EVENT = "announcementVideoProgress" as const;

export interface FlushLessonVideoProgressParams {
	enrollmentId: number;
	progress: number;
	courseSlug: string;
	lessonSlug: string;
}

/**
 * Persist last known watch position: WebSocket when available, else PUT /lessons/.../video-time
 * (same as real-time path). Invalidates the lesson query so the next load shows videoTime.
 * Call on unmount (e.g. navigate away).
 */
export const useFlushLessonVideoProgress = () => {
	const {socket} = useWebSocket();
	const queryClient = useQueryClient();
	const axiosConfig = useImproveAxios();

	return useCallback(
		(params: FlushLessonVideoProgressParams): void => {
			if (!params.enrollmentId || params.progress == null) return;
			const invalidate = (): void => {
				void queryClient.invalidateQueries({
					queryKey: getLessonForCourseQueryKey(params.courseSlug, params.lessonSlug)
				});
			};

			if (socket?.emit) {
				const message: LessonVideoProgressMessage = {
					enrollmentId: params.enrollmentId,
					progress: params.progress
				};
				socket.emit(LESSON_VIDEO_PROGRESS_SOCKET_EVENT, message);
				invalidate();
				return;
			}

			const {lessonsControllerSaveVideoTime} = getImproveAPI();
			const time = Math.round(params.progress);
			void lessonsControllerSaveVideoTime(params.enrollmentId, {time}, axiosConfig)
				.then(invalidate)
				.catch(() => {
					/* best effort on unmount */
				});
		},
		[socket, queryClient, axiosConfig]
	);
};

export interface FlushAnnouncementVideoProgressParams {
	enrollmentId: number;
	progress: number;
}

/**
 * Persist last known announcement watch position via WebSocket.
 * Patches learner-assigned / notifications cache so the next open shows videoTime.
 */
export const useFlushAnnouncementVideoProgress = () => {
	const {socket} = useWebSocket();
	const queryClient = useQueryClient();

	return useCallback(
		(params: FlushAnnouncementVideoProgressParams): void => {
			if (!params.enrollmentId || params.progress == null) return;

			if (socket?.emit) {
				const message: AnnouncementVideoProgressMessage = {
					enrollmentId: params.enrollmentId,
					progress: params.progress
				};
				socket.emit(ANNOUNCEMENT_VIDEO_PROGRESS_SOCKET_EVENT, message);
				patchLearnerAssignedEnrollmentVideoTime(
					queryClient,
					params.enrollmentId,
					params.progress
				);
				patchLearnerNotificationEnrollmentVideoTime(
					queryClient,
					params.enrollmentId,
					params.progress
				);
			}
		},
		[socket, queryClient]
	);
};
